import { apiClient } from '@/config/api';

const BASE = '/api/v1/admin/integrations/digital-signatures';

export type DigitalSignatureIntegrationStatus = 'ready' | 'warnings' | 'blockers';

export interface DigitalSignatureOnboardingSection {
  key: string;
  title: string;
  status: DigitalSignatureIntegrationStatus;
  details: string;
  action_label: string;
}

export interface DigitalSignaturePendingSigner {
  user_id: string;
  full_name: string;
  email: string | null;
  role: string;
  missing_items: string[];
}

export interface DigitalSignatureOnboardingSummary {
  overall_status: DigitalSignatureIntegrationStatus;
  completed_checks: number;
  total_checks: number;
  completion_percent: number;
  lawyer_readiness: {
    attorney_total: number;
    signer_ready: number;
    signer_pending: number;
    pending_users: DigitalSignaturePendingSigner[];
  };
  sections: DigitalSignatureOnboardingSection[];
}

export interface DigitalSignatureOverview {
  policies: {
    total: number;
    active: number;
  };
  certificates: {
    total: number;
    active: number;
  };
  sessions: {
    active: number;
    validated_pending_commit: number;
    committed_last_24h: number;
  };
  onboarding?: DigitalSignatureOnboardingSummary;
  checked_at: string;
}

export interface DigitalSignatureDependencyCheck {
  name: string;
  binary: string;
  required: boolean;
  available: boolean;
  version: string | null;
  notes: string;
}

export interface DigitalSignatureDependenciesResponse {
  status: DigitalSignatureIntegrationStatus;
  checks: DigitalSignatureDependencyCheck[];
  checked_at: string;
}

export interface SignaturePolicyProfile {
  profile_id?: string;
  realm_id?: string;
  profile_key: string;
  display_name: string;
  description?: string | null;
  signature_mode?: string;
  require_flatten?: boolean;
  require_evidence?: boolean;
  require_crypto_signature?: boolean;
  allow_drawn_signature?: boolean;
  allow_typed_signature?: boolean;
  allow_uploaded_signature_image?: boolean;
  is_default?: boolean;
  is_active?: boolean;
  policy_json?: Record<string, unknown> | null;
}

export interface SignatureCertificateProfile {
  certificate_id?: string;
  realm_id?: string;
  alias: string;
  provider?: string;
  mode?: string;
  external_key_ref?: string | null;
  display_name?: string | null;
  subject_cn?: string | null;
  issuer_cn?: string | null;
  expires_at?: string | null;
  is_active?: boolean;
  metadata_json?: Record<string, unknown> | null;
}

export interface DigitalSignatureValidationResponse {
  status: DigitalSignatureIntegrationStatus;
  dependency_status: DigitalSignatureIntegrationStatus;
  issues: Array<{ severity: 'blocker' | 'warning'; message: string }>;
  checked_at: string;
}

const unwrapData = <T>(payload: any): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload.data as T;
  }
  return payload as T;
};

export const digitalSignaturesApi = {
  async getOverview(): Promise<DigitalSignatureOverview> {
    const response = await apiClient.get(`${BASE}/overview`);
    return unwrapData<DigitalSignatureOverview>(response);
  },

  async getDependencies(): Promise<DigitalSignatureDependenciesResponse> {
    const response = await apiClient.get(`${BASE}/dependencies`);
    return unwrapData<DigitalSignatureDependenciesResponse>(response);
  },

  async getPolicies(): Promise<SignaturePolicyProfile[]> {
    const response = await apiClient.get(`${BASE}/policies`);
    return unwrapData<SignaturePolicyProfile[]>(response);
  },

  async savePolicies(policies: SignaturePolicyProfile[]): Promise<SignaturePolicyProfile[]> {
    const response = await apiClient.put(`${BASE}/policies`, { policies });
    return unwrapData<SignaturePolicyProfile[]>(response);
  },

  async getCertificates(): Promise<SignatureCertificateProfile[]> {
    const response = await apiClient.get(`${BASE}/certificates`);
    return unwrapData<SignatureCertificateProfile[]>(response);
  },

  async saveCertificates(certificates: SignatureCertificateProfile[]): Promise<SignatureCertificateProfile[]> {
    const response = await apiClient.put(`${BASE}/certificates`, { certificates });
    return unwrapData<SignatureCertificateProfile[]>(response);
  },

  async validateConfiguration(input: {
    policy_profile_id?: string;
    certificate_alias?: string;
  }): Promise<DigitalSignatureValidationResponse> {
    const response = await apiClient.post(`${BASE}/validate`, input || {});
    return unwrapData<DigitalSignatureValidationResponse>(response);
  },
};
